// src/components/AdminSideComponents/DashBoardProduct/BulkUploadModal.js

import React, { useState } from "react";
import { X, Upload, FileSpreadsheet, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";

const BulkUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Confirm
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setFile(selectedFile);
    parseExcelFile(selectedFile);
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Process and validate data
        const processedData = jsonData.map((row, index) => ({
          rowNumber: index + 2, // +2 because row 1 is header
          sku: row["SKU"] || "",
          name: row["Product Name"] || "",
          description: row["Description"] || "",
          category: row["Category Name"] || "",
          subCategory: row["Sub-Category Name"] || "",
          material: row["Material"] || "",
          height: row["Height"] || "",
          weight: row["Weight"] || "",
          measurement: row["Measurement"] || "",
          finishType: row["Finish Type"] || "",
          origin: row["Origin"] || "",
          brand: row["Brand"] || "BHK Handicrafts",
          productType: row["Product Type"] || "",
          price: parseFloat(row["Price (INR)"]) || 0,
          previous_price: parseFloat(row["Previous Price / MRP (INR)"]) || 0,
          gstPercentage: parseFloat(row["GST %"]) || 0,
          shippingCharge: parseFloat(row["Shipping Charge (INR)"]) || 0,
          images: row["Image URLs (comma-separated)"] || "",
          productVideo: row["Product Video URL"] || "",
          feature1: row["Feature 1"] || "",
          feature2: row["Feature 2"] || "",
          feature3: row["Feature 3"] || "",
          feature4: row["Feature 4"] || "",
          careInstruction: row["Care Instruction"] || "",
          returnPolicy: row["Return Policy"] || "",
          hasWarranty: row["Has Warranty (true / false)"] === "true" || row["Has Warranty (true / false)"] === true,
          warrantyDays: row["Warranty Days"] || "",
          warrantyDescription: row["Warranty Description"] || "",
          paymentTerms: row["Payment Terms"] || "",
          deliveryTime: row["Delivery Time"] || "",
          supplyAbility: row["Supply Ability"] || "",
          mainDomesticMarket: row["Main Domestic Market"] || "",
          stockStatus: row["Stock Status (active / inactive)"] || "active",
          isBestProduct: row["Is Best Product (true / false)"] === "true" || row["Is Best Product (true / false)"] === true,
          isSpecialProduct: row["Is Special Product (true / false)"] === "true" || row["Is Special Product (true / false)"] === true,
          ratings: parseFloat(row["Ratings (0 – 5)"]) || 0,
          uploadedBy: row["Uploaded By"] || "admin",
        }));

        setPreviewData(processedData);
        setStep(2);
        toast.success(`Successfully parsed ${processedData.length} products`);
      } catch (error) {
        console.error("Error parsing Excel:", error);
        toast.error("Failed to parse Excel file. Please check the format.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkUpload = async () => {
    setUploading(true);
    setUploadProgress(0);
    const results = {
      success: [],
      failed: [],
    };

    try {
      // First, get all existing SKUs to check for duplicates
      const existingProducts = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/get-all-products`
      );
      const existingSkus = new Set(
        existingProducts.data?.products?.map((p) => p.sku) || []
      );

      // Process each product
      for (let i = 0; i < previewData.length; i++) {
        const product = previewData[i];
        setUploadProgress(((i + 1) / previewData.length) * 100);

        try {
          // Validate required fields
          if (!product.sku || !product.name || !product.description) {
            results.failed.push({
              row: product.rowNumber,
              sku: product.sku || "N/A",
              error: "Missing required fields: SKU, Name, or Description",
            });
            continue;
          }

          // Check for duplicate SKU
          if (existingSkus.has(product.sku)) {
            results.failed.push({
              row: product.rowNumber,
              sku: product.sku,
              error: "SKU already exists in database",
            });
            continue;
          }

          // Get or create category
          const categoryId = await getOrCreateCategory(product.category);
          if (!categoryId) {
            results.failed.push({
              row: product.rowNumber,
              sku: product.sku,
              error: `Category "${product.category}" not found and could not be created`,
            });
            continue;
          }

          // Get or create subcategory
          const subCategoryId = await getOrCreateSubCategory(
            product.subCategory,
            categoryId
          );
          if (!subCategoryId) {
            results.failed.push({
              row: product.rowNumber,
              sku: product.sku,
              error: `SubCategory "${product.subCategory}" not found and could not be created`,
            });
            continue;
          }

          // Process images
          const imageUrls = product.images
            ? product.images.split(",").map((url) => url.trim())
            : [];

          // Prepare product data
          const productData = {
            sku: product.sku,
            name: {
              en: product.name,
              hi: product.name,
              es: product.name,
              zh: product.name,
            },
            description: {
              en: product.description,
              hi: product.description,
              es: product.description,
              zh: product.description,
            },
            category: categoryId,
            subCategory: subCategoryId,
            material: {
              en: product.material || "",
              hi: product.material || "",
              es: product.material || "",
              zh: product.material || "",
            },
            height: product.height || "",
            weight: product.weight || "",
            measurement: {
              en: product.measurement || "",
              hi: product.measurement || "",
              es: product.measurement || "",
              zh: product.measurement || "",
            },
            finishType: {
              en: product.finishType || "",
              hi: product.finishType || "",
              es: product.finishType || "",
              zh: product.finishType || "",
            },
            origin: {
              en: product.origin || "Made in India",
              hi: product.origin || "भारत में निर्मित",
              es: product.origin || "Hecho en India",
              zh: product.origin || "印度制造",
            },
            brand: {
              en: product.brand || "",
              hi: product.brand || "",
              es: product.brand || "",
              zh: product.brand || "",
            },
            productType: {
              en: product.productType || "",
              hi: product.productType || "",
              es: product.productType || "",
              zh: product.productType || "",
            },
            price: product.price,
            previous_price: product.previous_price,
            gstPercentage: product.gstPercentage || 18,
            shippingCharge: product.shippingCharge || 0,
            images: imageUrls,
            productVideo: product.productVideo || null,
            features: [
              product.feature1,
              product.feature2,
              product.feature3,
              product.feature4,
            ]
              .filter((f) => f && f.trim())
              .map((f) => ({
                en: f,
                hi: f,
                es: f,
                zh: f,
              })),
            careInstruction: {
              en: product.careInstruction || "",
              hi: product.careInstruction || "",
              es: product.careInstruction || "",
              zh: product.careInstruction || "",
            },
            return_policy: {
              en: product.returnPolicy || "7 days return policy",
              hi: product.returnPolicy || "7 दिन की वापसी नीति",
              es: product.returnPolicy || "Política de devolución de 7 días",
              zh: product.returnPolicy || "7天退货政策",
            },
            warranty: {
              hasWarranty: product.hasWarranty || false,
              warrantyDays: product.warrantyDays || "0 days",
              description: {
                en: product.warrantyDescription || "",
                hi: product.warrantyDescription || "",
                es: product.warrantyDescription || "",
                zh: product.warrantyDescription || "",
              },
            },
            tradeInfo: {
              paymentTerms: {
                en: product.paymentTerms || "Cash on Delivery available",
                hi: product.paymentTerms || "कैश ऑन डिलीवरी उपलब्ध",
                es: product.paymentTerms || "Contra reembolso disponible",
                zh: product.paymentTerms || "货到付款",
              },
              deliveryTime: {
                en: product.deliveryTime || "4-6 business days",
                hi: product.deliveryTime || "4-6 कार्य दिवस",
                es: product.deliveryTime || "4-6 días hábiles",
                zh: product.deliveryTime || "4-6个工作日",
              },
              supplyAbility: {
                en: product.supplyAbility || "",
                hi: product.supplyAbility || "",
                es: product.supplyAbility || "",
                zh: product.supplyAbility || "",
              },
              mainDomesticMarket: {
                en: product.mainDomesticMarket || "",
                hi: product.mainDomesticMarket || "",
                es: product.mainDomesticMarket || "",
                zh: product.mainDomesticMarket || "",
              },
            },
            stock_status: product.stockStatus || "active",
            isBestProduct: product.isBestProduct || false,
            is_special_product: product.isSpecialProduct || false,
            ratings: product.ratings || 0,
            uploadedBy: product.uploadedBy || "admin",
          };

          // Create product via API
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/products/create-product`,
            productData
          );

          if (response.data?.message === "Product created successfully") {
            results.success.push({
              row: product.rowNumber,
              sku: product.sku,
              name: product.name,
            });
            existingSkus.add(product.sku);
          } else {
            results.failed.push({
              row: product.rowNumber,
              sku: product.sku,
              error: response.data?.message || "Unknown error",
            });
          }
        } catch (error) {
          console.error(`Error creating product ${product.sku}:`, error);
          results.failed.push({
            row: product.rowNumber,
            sku: product.sku || "N/A",
            error: error.response?.data?.message || error.message || "Failed to create product",
          });
        }
      }

      setUploadResults(results);
      setStep(3);
      setUploading(false);

      // Show summary
      if (results.success.length > 0) {
        toast.success(`✅ ${results.success.length} products uploaded successfully!`);
      }
      if (results.failed.length > 0) {
        toast.error(`❌ ${results.failed.length} products failed to upload`);
      }

      if (results.success.length > 0 && onUploadSuccess) {
        onUploadSuccess();
      }
    } catch (error) {
      console.error("Bulk upload error:", error);
      toast.error("Bulk upload failed. Please try again.");
      setUploading(false);
    }
  };

  const getOrCreateCategory = async (categoryName) => {
    if (!categoryName) return null;

    try {
      // Check if category exists
      const categories = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
      );
      const existing = categories.data.find(
        (c) => c.name.en?.toLowerCase() === categoryName.toLowerCase()
      );

      if (existing) return existing._id;

      // Create new category
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/categories/create-category`,
        {
          name: {
            en: categoryName,
            hi: categoryName,
            es: categoryName,
            zh: categoryName,
          },
          imgUrl: "",
        }
      );
      return response.data?.category?._id || null;
    } catch (error) {
      console.error("Error creating category:", error);
      return null;
    }
  };

  const getOrCreateSubCategory = async (subCategoryName, categoryId) => {
    if (!subCategoryName || !categoryId) return null;

    try {
      // Check if subcategory exists
      const subCategories = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
      );
      const existing = subCategories.data.find(
        (sc) =>
          sc.name.en?.toLowerCase() === subCategoryName.toLowerCase() &&
          sc.category._id === categoryId
      );

      if (existing) return existing._id;

      // Create new subcategory
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sub-categories/create-sub-category`,
        {
          name: {
            en: subCategoryName,
            hi: subCategoryName,
            es: subCategoryName,
            zh: subCategoryName,
          },
          category: categoryId,
          imgUrl: "",
        }
      );
      return response.data?.subCategory?._id || null;
    } catch (error) {
      console.error("Error creating subcategory:", error);
      return null;
    }
  };

  const resetModal = () => {
    setFile(null);
    setPreviewData([]);
    setStep(1);
    setUploadProgress(0);
    setUploadResults(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-philosopher text-gray-800">
              Bulk Upload Products
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-blue-800">Instructions</h3>
                    <ul className="text-sm text-blue-700 list-disc list-inside space-y-1 mt-1">
                      <li>Download the template from the admin panel</li>
                      <li>
                        Fill in the required fields (marked in yellow in the
                        template)
                      </li>
                      <li>
                        Upload the filled Excel file (.xlsx or .xls format)
                      </li>
                      <li>
                        Review the preview before confirming the upload
                      </li>
                      <li>
                        Maximum 500 products per upload for optimal performance
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-green-500 transition">
                <input
                  type="file"
                  id="excelUpload"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="excelUpload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-gray-600">
                    Drop your Excel file here or{" "}
                    <span className="text-green-600 font-medium">
                      click to browse
                    </span>
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Supports .xlsx and .xls formats
                  </p>
                </label>
              </div>

              {file && (
                <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-green-600" />
                    <div>
                      <p className="font-medium text-gray-700">{file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFile(null);
                      setPreviewData([]);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!file) {
                      toast.error("Please select a file first");
                      return;
                    }
                    if (previewData.length === 0) {
                      toast.error("No data found in the file");
                      return;
                    }
                    setStep(2);
                  }}
                  disabled={!file || previewData.length === 0}
                  className="px-6 py-2 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Preview Data ({previewData.length})
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-800">
                    Preview: {previewData.length} products found
                  </h3>
                  <p className="text-sm text-gray-500">
                    Review the data before uploading
                  </p>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  ← Back to upload
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left">#</th>
                        <th className="px-4 py-2 text-left">SKU</th>
                        <th className="px-4 py-2 text-left">Name</th>
                        <th className="px-4 py-2 text-left">Category</th>
                        <th className="px-4 py-2 text-left">Price</th>
                        <th className="px-4 py-2 text-left">Stock</th>
                        <th className="px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.slice(0, 20).map((product, index) => {
                        const hasErrors = !product.sku || !product.name || !product.description;
                        return (
                          <tr
                            key={index}
                            className={`border-b ${hasErrors ? "bg-red-50" : ""}`}
                          >
                            <td className="px-4 py-2">{product.rowNumber}</td>
                            <td className="px-4 py-2 font-mono text-xs">
                              {product.sku || "❌ Missing"}
                            </td>
                            <td className="px-4 py-2">
                              {product.name || "❌ Missing"}
                            </td>
                            <td className="px-4 py-2">
                              {product.category || "❌ Missing"}
                            </td>
                            <td className="px-4 py-2">₹{product.price}</td>
                            <td className="px-4 py-2">
                              <span
                                className={`px-2 py-1 rounded-full text-xs ${
                                  product.stockStatus === "active"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {product.stockStatus}
                              </span>
                            </td>
                            <td className="px-4 py-2">
                              {hasErrors ? (
                                <span className="text-red-500 text-xs">
                                  ⚠️ Missing required fields
                                </span>
                              ) : (
                                <span className="text-green-500 text-xs">✅ Valid</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {previewData.length > 20 && (
                    <div className="p-4 text-center text-sm text-gray-500 border-t">
                      Showing 20 of {previewData.length} products
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  {previewData.filter((p) => p.sku && p.name && p.description)
                    .length > 0 ? (
                    <span className="text-green-600">
                      ✅{" "}
                      {
                        previewData.filter((p) => p.sku && p.name && p.description)
                          .length
                      }{" "}
                      valid products ready to upload
                    </span>
                  ) : (
                    <span className="text-red-600">
                      ⚠️ No valid products found. Please check the data.
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleBulkUpload}
                    disabled={
                      uploading ||
                      previewData.filter((p) => p.sku && p.name && p.description)
                        .length === 0
                    }
                    className="px-6 py-2 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                        Uploading... {Math.round(uploadProgress)}%
                      </>
                    ) : (
                      "Upload All Products"
                    )}
                  </button>
                </div>
              </div>

              {uploading && (
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-[#b07d17] h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          )}

          {step === 3 && uploadResults && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {uploadResults.success.length > 0 ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-red-600" />
                )}
                <div>
                  <h3 className="font-medium text-gray-800">Upload Complete</h3>
                  <p className="text-sm text-gray-500">
                    {uploadResults.success.length} successful,{" "}
                    {uploadResults.failed.length} failed
                  </p>
                </div>
              </div>

              {uploadResults.success.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-800 mb-2">
                    ✅ Successfully Uploaded ({uploadResults.success.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto">
                    {uploadResults.success.map((item, index) => (
                      <div
                        key={index}
                        className="text-sm text-green-700 py-1 border-b border-green-100"
                      >
                        Row {item.row}: {item.name} (SKU: {item.sku})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {uploadResults.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-800 mb-2">
                    ❌ Failed ({uploadResults.failed.length})
                  </h4>
                  <div className="max-h-40 overflow-y-auto">
                    {uploadResults.failed.map((item, index) => (
                      <div
                        key={index}
                        className="text-sm text-red-700 py-1 border-b border-red-100"
                      >
                        Row {item.row}: {item.sku} - {item.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-[#b07d17] text-white rounded-lg hover:bg-[#8c6312] transition"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;